import React, { useState } from 'react';

interface HelpModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type TabType = 'general' | 'organizador' | 'jugador' | 'reportes' | 'tecnico';

export const HelpModal: React.FC<HelpModalProps> = ({ isOpen, onClose }) => {
  const [activeTab, setActiveTab] = useState<TabType>('general');

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  const tabs = [
    { id: 'general', label: '🎯 General', icon: '🎯' },
    { id: 'organizador', label: '🏗️ Organizador', icon: '🏗️' },
    { id: 'jugador', label: '⚽ Jugador', icon: '⚽' },
    { id: 'reportes', label: '📊 Reportes', icon: '📊' },
    { id: 'tecnico', label: '🔧 Técnico', icon: '🔧' }
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'general':
        return (
          <div>
            <div style={{ marginBottom: '28px' }}>
              <h3 style={{ color: '#60A5FA', marginBottom: '12px', fontSize: '20px', fontWeight: 700, letterSpacing: '0.2px' }}>🎯 ¿Qué es Futbolero?</h3>
              <p style={{ marginBottom: '16px', lineHeight: '1.6' }}>
                Futbolero es una aplicación web que simplifica la organización de partidos de fútbol 5, 7 u 11. 
                Permite crear partidos, gestionar equipos, controlar pagos y mantener todo organizado en tiempo real.
              </p>
            </div>

            <div style={{ marginBottom: '28px' }}>
              <h3 style={{ color: '#34D399', marginBottom: '12px', fontSize: '20px', fontWeight: 700, letterSpacing: '0.2px' }}>⚽ Funciones Principales</h3>
              <ul style={{ margin: 0, paddingLeft: '22px' }}>
                <li><strong>Crear Partido:</strong> Organiza un nuevo partido con fecha, hora, ubicación y costo</li>
                <li><strong>Unirse a Partido:</strong> Inscríbete como jugador principal o suplente</li>
                <li><strong>Ver Partido:</strong> Consulta detalles, jugadores inscritos y equipos</li>
                <li><strong>Gestionar Equipos:</strong> Formación automática y manual con drag & drop</li>
                <li><strong>Sistema de Reportes:</strong> Auto-reporte de estadísticas post-partido</li>
                <li><strong>Sistema de Reviews:</strong> Evaluaciones mutuas entre jugadores</li>
              </ul>
            </div>

            <div style={{ marginBottom: '28px' }}>
              <h3 style={{ color: '#F87171', marginBottom: '12px', fontSize: '20px', fontWeight: 700, letterSpacing: '0.2px' }}>📱 Características Técnicas</h3>
              <ul style={{ margin: 0, paddingLeft: '22px' }}>
                <li><strong>Tiempo Real:</strong> Actualizaciones automáticas vía WebSocket</li>
                <li><strong>Responsive:</strong> Funciona en móviles y desktop</li>
                <li><strong>Notificaciones:</strong> Alertas para eventos importantes</li>
                <li><strong>Integración WhatsApp:</strong> Bot para notificaciones automáticas</li>
                <li><strong>Autenticación Google:</strong> Login seguro y confiable</li>
              </ul>
            </div>

            <div style={{ 
              padding: '18px', 
              backgroundColor: '#1E293B', 
              borderRadius: '10px', 
              border: '1.5px solid #60A5FA',
              marginTop: '28px',
              color: '#E0F2FE',
              fontSize: '15px'
            }}>
              <h4 style={{ color: '#38BDF8', margin: '0 0 8px 0', fontWeight: 700 }}>💡 Consejos Generales</h4>
              <ul style={{ margin: 0, paddingLeft: '20px' }}>
                <li>Comparte el código del partido para que otros se unan</li>
                <li>Usa el link de Google Maps para ver la ubicación exacta</li>
                <li>Los suplentes entran automáticamente si alguien se baja</li>
                <li>Mantén actualizado el estado de pagos para mejor organización</li>
                <li>Reporta tus estadísticas después de cada partido</li>
              </ul>
            </div>
          </div>
        );

      case 'organizador':
        return (
          <div>
            <div style={{ marginBottom: '28px' }}>
              <h3 style={{ color: '#60A5FA', marginBottom: '12px', fontSize: '20px', fontWeight: 700, letterSpacing: '0.2px' }}>🏗️ Panel de Control Principal</h3>
              <ul style={{ margin: 0, paddingLeft: '22px' }}>
                <li><strong>Header del Partido:</strong> Información básica, estado y acciones rápidas</li>
                <li><strong>Gestionar Equipos:</strong> Formación automática y manual con drag & drop</li>
                <li><strong>Control de Jugadores:</strong> Ver inscritos, promover suplentes, remover jugadores</li>
                <li><strong>Editar Partido:</strong> Modificar fecha, hora, ubicación y nombres de equipos</li>
              </ul>
            </div>

            <div style={{ marginBottom: '28px' }}>
              <h3 style={{ color: '#34D399', marginBottom: '12px', fontSize: '20px', fontWeight: 700, letterSpacing: '0.2px' }}>⚽ Gestión de Equipos</h3>
              <ul style={{ margin: 0, paddingLeft: '22px' }}>
                <li><strong>Formación Automática:</strong> Los equipos se forman solos al unirse jugadores</li>
                <li><strong>Drag & Drop:</strong> Arrastra jugadores entre equipos para reorganizar</li>
                <li><strong>Equilibrio:</strong> Los equipos se mantienen balanceados por rating</li>
                <li><strong>Nombres Personalizables:</strong> Cambia "Equipo 1" y "Equipo 2" por nombres reales</li>
              </ul>
            </div>

            <div style={{ marginBottom: '28px' }}>
              <h3 style={{ color: '#A78BFA', marginBottom: '12px', fontSize: '20px', fontWeight: 700, letterSpacing: '0.2px' }}>🔧 Funciones de Administrador</h3>
              <ul style={{ margin: 0, paddingLeft: '22px' }}>
                <li><strong>Transferir Admin:</strong> Cede la administración a otro jugador</li>
                <li><strong>Importar Lista:</strong> Agrega jugadores desde una lista existente</li>
                <li><strong>Finalizar Partido:</strong> Marca el partido como terminado</li>
                <li><strong>Exportar Datos:</strong> Obtén información del partido</li>
              </ul>
            </div>

            <div style={{ 
              padding: '18px', 
              backgroundColor: '#1E293B', 
              borderRadius: '10px', 
              border: '1.5px solid #34D399',
              marginTop: '28px',
              color: '#E0F2FE',
              fontSize: '15px'
            }}>
              <h4 style={{ color: '#34D399', margin: '0 0 8px 0', fontWeight: 700 }}>💡 Consejos para Organizadores</h4>
              <ul style={{ margin: 0, paddingLeft: '20px' }}>
                <li>Crea partidos con anticipación para dar tiempo a la inscripción</li>
                <li>Usa ubicaciones con links de Google Maps para facilitar la llegada</li>
                <li>Mantén actualizado el estado de pagos</li>
                <li>Comunica cambios importantes a los jugadores</li>
                <li>Usa el sistema de suplentes para cubrir bajas</li>
              </ul>
            </div>
          </div>
        );

      case 'jugador':
        return (
          <div>
            <div style={{ marginBottom: '28px' }}>
              <h3 style={{ color: '#60A5FA', marginBottom: '12px', fontSize: '20px', fontWeight: 700, letterSpacing: '0.2px' }}>⚽ Ver Información del Partido</h3>
              <ul style={{ margin: 0, paddingLeft: '22px' }}>
                <li><strong>Mi Equipo:</strong> Posición en el campo, compañeros y formación</li>
                <li><strong>Estado Personal:</strong> Mi pago, posición y rating actual</li>
                <li><strong>Notificaciones:</strong> Recibe alertas de cambios importantes</li>
                <li><strong>Chat en Tiempo Real:</strong> Comunicación vía WebSocket</li>
              </ul>
            </div>

            <div style={{ marginBottom: '28px' }}>
              <h3 style={{ color: '#34D399', marginBottom: '12px', fontSize: '20px', fontWeight: 700, letterSpacing: '0.2px' }}>🎯 Proceso de Inscripción</h3>
              <ul style={{ margin: 0, paddingLeft: '22px' }}>
                <li><strong>Recibir Código:</strong> El organizador te envía un código único</li>
                <li><strong>Completar Perfil:</strong> Nombre, posición, rating y tipo de jugador</li>
                <li><strong>Confirmar Inscripción:</strong> Haz clic en "Unirme al Partido"</li>
                <li><strong>Ver Equipos:</strong> Consulta tu equipo y compañeros</li>
              </ul>
            </div>

            <div style={{ marginBottom: '28px' }}>
              <h3 style={{ color: '#F87171', marginBottom: '12px', fontSize: '20px', fontWeight: 700, letterSpacing: '0.2px' }}>💰 Sistema de Pagos</h3>
              <ul style={{ margin: 0, paddingLeft: '22px' }}>
                <li><strong>Marcar Pago:</strong> Confirma cuando hayas pagado</li>
                <li><strong>Estado Visual:</strong> Colores indican el estado de tu pago</li>
                <li><strong>Transferencias:</strong> Soporte para CVU/CBU/Alias del organizador</li>
                <li><strong>Confirmación:</strong> El organizador valida tu pago</li>
              </ul>
            </div>

                         <div style={{ 
               padding: '18px', 
               backgroundColor: '#1E293B', 
               borderRadius: '10px', 
               border: '1.5px solid #F87171',
               marginTop: '28px',
               color: '#E0F2FE',
               fontSize: '15px'
             }}>
              <h4 style={{ color: '#F87171', margin: '0 0 8px 0', fontWeight: 700 }}>💡 Consejos para Jugadores</h4>
              <ul style={{ margin: 0, paddingLeft: '20px' }}>
                <li>Únete con tiempo para asegurar tu lugar</li>
                <li>Marca tu pago apenas lo hagas</li>
                <li>Reporta tus estadísticas después del partido</li>
                <li>Sé puntual y comunica si no puedes asistir</li>
                <li>Participa en el sistema de reviews para mejorar la comunidad</li>
              </ul>
            </div>
          </div>
        );

      case 'reportes':
        return (
          <div>
            <div style={{ marginBottom: '28px' }}>
              <h3 style={{ color: '#60A5FA', marginBottom: '12px', fontSize: '20px', fontWeight: 700, letterSpacing: '0.2px' }}>📊 Sistema de Auto-Reportes</h3>
              <ul style={{ margin: 0, paddingLeft: '22px' }}>
                <li><strong>Estadísticas Básicas:</strong> Goles, asistencias y saves (para arqueros)</li>
                <li><strong>Cuándo Reportar:</strong> Post-partido, máximo 24h después</li>
                <li><strong>Proceso:</strong> Accede al sistema, completa el formulario y envía</li>
                <li><strong>Validación:</strong> Otros jugadores confirman tu reporte</li>
              </ul>
            </div>

            <div style={{ marginBottom: '28px' }}>
              <h3 style={{ color: '#34D399', marginBottom: '12px', fontSize: '20px', fontWeight: 700, letterSpacing: '0.2px' }}>⭐ Sistema de Reviews</h3>
              <ul style={{ margin: 0, paddingLeft: '22px' }}>
                <li><strong>Habilidad Técnica:</strong> Control, pase, defensa y ataque (1-5 estrellas)</li>
                <li><strong>Actitud y Comportamiento:</strong> Espíritu deportivo, comunicación, puntualidad</li>
                <li><strong>Comentarios:</strong> Texto libre y constructivo</li>
                <li><strong>Proceso:</strong> Selecciona jugador, evalúa por categorías y envía</li>
              </ul>
            </div>

            <div style={{ marginBottom: '28px' }}>
              <h3 style={{ color: '#A78BFA', marginBottom: '12px', fontSize: '20px', fontWeight: 700, letterSpacing: '0.2px' }}>📈 Mi Perfil y Reputación</h3>
              <ul style={{ margin: 0, paddingLeft: '22px' }}>
                <li><strong>Reviews Recibidas:</strong> Historial de evaluaciones y promedio</li>
                <li><strong>Estadísticas Personales:</strong> Partidos, goles, asistencias y rating</li>
                <li><strong>Badges y Logros:</strong> Goleador, asistente, deportista, puntual</li>
                <li><strong>Tendencia:</strong> Ve si vas mejorando en el tiempo</li>
              </ul>
            </div>

            <div style={{ 
              padding: '18px', 
              backgroundColor: '#1E293B', 
              borderRadius: '10px', 
              border: '1.5px solid #A78BFA',
              marginTop: '28px',
              color: '#E0F2FE',
              fontSize: '15px'
            }}>
              <h4 style={{ color: '#A78BFA', margin: '0 0 8px 0', fontWeight: 700 }}>💡 Consejos para un Sistema Justo</h4>
              <ul style={{ margin: 0, paddingLeft: '20px' }}>
                <li>Sé honesto al reportar estadísticas</li>
                <li>Evalúa objetivamente basándote en hechos</li>
                <li>Mantén respeto en comentarios y reviews</li>
                <li>Considera el contexto y nivel del jugador</li>
                <li>Usa feedback constructivo para mejorar</li>
              </ul>
            </div>
          </div>
        );

      case 'tecnico':
        return (
          <div>
            <div style={{ marginBottom: '28px' }}>
              <h3 style={{ color: '#60A5FA', marginBottom: '12px', fontSize: '20px', fontWeight: 700, letterSpacing: '0.2px' }}>🔧 Funcionalidades Avanzadas</h3>
              <ul style={{ margin: 0, paddingLeft: '22px' }}>
                <li><strong>WebSocket:</strong> Actualizaciones en tiempo real</li>
                <li><strong>Responsive Design:</strong> Funciona en móviles y desktop</li>
                <li><strong>Drag & Drop:</strong> Interfaz intuitiva para formar equipos</li>
                <li><strong>Notificaciones Push:</strong> Alertas instantáneas</li>
                <li><strong>Integración WhatsApp:</strong> Bot para notificaciones automáticas</li>
              </ul>
            </div>

            <div style={{ marginBottom: '28px' }}>
              <h3 style={{ color: '#34D399', marginBottom: '12px', fontSize: '20px', fontWeight: 700, letterSpacing: '0.2px' }}>🛡️ Seguridad y Privacidad</h3>
              <ul style={{ margin: 0, paddingLeft: '22px' }}>
                <li><strong>Autenticación Google:</strong> Login seguro y confiable</li>
                <li><strong>Tokens JWT:</strong> Sesiones seguras</li>
                <li><strong>Validación de Datos:</strong> Verificación de información</li>
                <li><strong>Permisos Granulares:</strong> Diferentes niveles de acceso</li>
              </ul>
            </div>

            <div style={{ marginBottom: '28px' }}>
              <h3 style={{ color: '#F87171', marginBottom: '12px', fontSize: '20px', fontWeight: 700, letterSpacing: '0.2px' }}>🚀 Próximas Funcionalidades</h3>
              <ul style={{ margin: 0, paddingLeft: '22px' }}>
                <li><strong>Sistema de Rankings:</strong> Clasificación por habilidad</li>
                <li><strong>Torneos:</strong> Organización de competencias</li>
                <li><strong>Estadísticas Avanzadas:</strong> Análisis de rendimiento</li>
                <li><strong>Gamificación:</strong> Badges y logros</li>
                <li><strong>Integración con Apps:</strong> Calendario y recordatorios</li>
              </ul>
            </div>

            <div style={{ 
              padding: '18px', 
              backgroundColor: '#1E293B', 
              borderRadius: '10px', 
              border: '1.5px solid #FB923C',
              marginTop: '28px',
              color: '#E0F2FE',
              fontSize: '15px'
            }}>
              <h4 style={{ color: '#FB923C', margin: '0 0 8px 0', fontWeight: 700 }}>💡 Información Técnica</h4>
              <ul style={{ margin: 0, paddingLeft: '20px' }}>
                <li>La app funciona en todos los navegadores modernos</li>
                <li>Los datos se guardan automáticamente</li>
                <li>Las notificaciones funcionan en tiempo real</li>
                <li>El sistema es escalable para muchos usuarios</li>
                <li>Soporte para múltiples dispositivos</li>
              </ul>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        animation: 'fadeIn 0.2s ease-out'
      }}
      onClick={handleOverlayClick}
      onKeyDown={(e) => { if (e.key === 'Escape') handleOverlayClick(e as any); }}
      role="presentation"
    >
      <div
        style={{
          backgroundColor: '#23272A',
          borderRadius: '16px',
          padding: '32px 28px',
          maxWidth: '800px',
          width: '95%',
          maxHeight: '85vh',
          overflowY: 'auto',
          boxShadow: '0 10px 40px rgba(0, 0, 0, 0.5)',
          animation: 'slideInScale 0.3s ease-out',
          border: '1.5px solid #33383B',
          color: '#F3F4F6',
          position: 'relative',
        }}
      >
        <div style={{ marginBottom: '20px' }}>
          <h2 style={{
            margin: '0 0 16px 0',
            fontSize: '26px',
            fontWeight: 800,
            color: '#F3F4F6',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            letterSpacing: '0.5px',
            textShadow: '0 2px 8px #181A1B88'
          }}>
            <span style={{ fontSize: '30px' }}>❓</span>
            Guía Completa - Futbolero
          </h2>
          <button
            onClick={onClose}
            style={{
              position: 'absolute',
              top: '18px',
              right: '18px',
              background: 'none',
              border: 'none',
              fontSize: '28px',
              cursor: 'pointer',
              color: '#B0BEC5',
              padding: '4px',
              borderRadius: '4px',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#2d3236';
              e.currentTarget.style.color = '#fff';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
              e.currentTarget.style.color = '#B0BEC5';
            }}
          >
            ×
          </button>
        </div>

        {/* Tabs */}
        <div style={{ 
          display: 'flex', 
          gap: '8px', 
          marginBottom: '24px',
          flexWrap: 'wrap'
        }}>
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as TabType)}
              style={{
                padding: '10px 16px',
                backgroundColor: activeTab === tab.id ? '#60A5FA' : '#374151',
                color: activeTab === tab.id ? '#fff' : '#D1D5DB',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 600,
                transition: 'all 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}
              onMouseEnter={(e) => {
                if (activeTab !== tab.id) {
                  e.currentTarget.style.backgroundColor = '#4B5563';
                }
              }}
              onMouseLeave={(e) => {
                if (activeTab !== tab.id) {
                  e.currentTarget.style.backgroundColor = '#374151';
                }
              }}
            >
              <span>{tab.icon}</span>
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div style={{ lineHeight: '1.7', fontSize: '16px', color: '#F3F4F6' }}>
          {renderTabContent()}
        </div>
      </div>
    </div>
  );
}; 